import moment from 'moment';

export const DATE = 'YYYY/MM/DD';

export const HYPHENED_DATE = 'YYYY-MM-DD';

export const MONTH_DAYS = 'M/D';

export const HYPHENED_MONTH_DAY = 'MM-DD';

export const TIME = 'HH:mm';

export const MINUTE_SECONDS = 'mm:ss';

export const TIME_SECONDS = `${TIME}:ss`;

export const DATE_TIME_SECONDS = `${DATE} ${TIME_SECONDS}`;

const FIGURE_DASH_PAIR = '\u2012\u2012';
const DURATION_PLACEHOLDER = `${FIGURE_DASH_PAIR}:${FIGURE_DASH_PAIR}:${FIGURE_DASH_PAIR}`;
const SHORT_DURATION_PLACEHOLDER = `${FIGURE_DASH_PAIR}:${FIGURE_DASH_PAIR}`;

export const searchSixMonthAgo = moment().subtract(180, 'days');

export function isDateValid(date) {
	return date ? moment(date).isValid() : false;
}

export function formatDate(date = new Date(), format = DATE_TIME_SECONDS) {
	return moment(date).format(format);
}

export function formatTimeSecs(seconds = 0, format = TIME_SECONDS) {
	return moment.utc(seconds * 1000).format(format);
}

export function formatShortDateTime(date) {
	if (!isDateValid(date)) {
		return { date: '--', time: '--' };
	}

	return {
		date: formatDate(date, HYPHENED_MONTH_DAY),
		time: formatDate(date, TIME),
	};
}

export const renderTime = (time, format = DATE_TIME_SECONDS) => time ? formatDate(time, format) : '--';

// 非 ISO 8601 字串不走 Date.parse:對其為實作定義行為,可能「解析成功但值錯」
// (如 'DD/MM/YYYY' 被當美式月日序),不會落入 NaN fallback
const ISO_8601_PATTERN = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

export function convertDateStringToTimestamp(dateString = '', format) {
	if (format) {
		return moment(dateString, format).valueOf();
	}

	return ISO_8601_PATTERN.test(dateString) ?
		Date.parse(dateString) :
		moment(dateString, DATE_TIME_SECONDS).valueOf();
}

export function formatDateISO8601(date) {
	return moment(date).toISOString();
}

export function getDurationFromTimestamp(dateTimestamp, nowTimestamp = Date.now()) {
	if (!Number.isFinite(dateTimestamp) || !Number.isFinite(nowTimestamp)) {
		return DURATION_PLACEHOLDER;
	}

	const duration = Math.max(0, Math.floor((nowTimestamp - dateTimestamp) / 1000));
	const seconds = duration % 60;
	const minutes = Math.floor(duration / 60) % 60;
	const hours = Math.floor(duration / 60 / 60);

	function display(digit) {
		return digit < 10 ? '0' + digit : digit;
	}

	return `${display(hours)}:${display(minutes)}:${display(seconds)}`;
}

export function getShortDurationFromTimestamp(dateTimestamp, nowTimestamp = Date.now()) {
	if (!Number.isFinite(dateTimestamp) || !Number.isFinite(nowTimestamp)) {
		return SHORT_DURATION_PLACEHOLDER;
	}

	const seconds = Math.max(0, Math.floor((nowTimestamp - dateTimestamp) / 1000));
	const minutes = Math.floor(seconds / 60);

	return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export function getToday(hourOffset = 0) {
	const today = moment();

	return today.clone().startOf('day').add(hourOffset, 'hour');
}

export function getNow() {
	return moment();
}

export function getLastDays(days = 1, hourOffset = 0) {
	const today = moment();

	return today.clone().subtract(days, 'days').startOf('day').add(hourOffset, 'hour');
}

export function getLastWeeks(weeks = 1, hourOffset = 0) {
	const today = moment();

	return today.clone().subtract(weeks, 'weeks').startOf('day').add(hourOffset, 'hour');
}

export function getLastMonths(months = 1, hourOffset = 0) {
	const today = moment();

	return today.clone().subtract(months, 'months').startOf('day').add(hourOffset, 'hour');
}

export function getTodayRange(hourOffset = 0) {
	const today = moment();

	return [
		today.clone().startOf('day').add(hourOffset, 'hour'),
		today.clone().endOf('day').add(hourOffset, 'hour'),
	];
}

export function getLastDaysRange(days, hourOffset = 0) {
	const today = moment();

	return [
		today.clone().subtract(days, 'days').startOf('day').add(hourOffset, 'hour'),
		today.clone().subtract(1, 'days').endOf('day').add(hourOffset, 'hour'),
	];
}

export function getLastMonthsRange(months, hourOffset = 0, startPoint = moment()) {
	const today = moment();

	return [
		startPoint.clone().subtract(months, 'months').startOf('day').add(hourOffset, 'hour'),
		today.clone().subtract(1, 'days').endOf('day').add(hourOffset, 'hour'),
	];
}

export function getMonthRange(months = 0) {
	const today = moment();

	return [
		today.clone().subtract(months, 'months').startOf('months'),
		today.clone().subtract(months, 'months').endOf('months'),
	];
}

const getComparedDate = (judegeDate, time) => moment(`${judegeDate} ${time}`);
const getIsExceptionDate = (exceptionDates, targetDate) => exceptionDates.find(date => date === targetDate);
const isDateSpanned = (startTime, endTime) => {
	const startDuration = moment(startTime, TIME);
	const endDuration = moment(endTime, TIME);

	return startDuration.isAfter(endDuration);
};

export function isBetweenRange({
	compared,
	startTime,
	endTime,
	dateRange,
	pattern = '[]',
}) {
	const judge = moment(compared);
	const judgeDate = judge.format(DATE);
	const from = getComparedDate(judgeDate, startTime);
	const to = getComparedDate(judgeDate, endTime);
	const isExceptionDate = getIsExceptionDate(['00:00'], endTime);

	if (isExceptionDate) {
		to.endOf('day');
	} else if (isDateSpanned(startTime, endTime)) {
		const [firstDate, lastDate] = dateRange;

		to.add(1, 'days');

		if (dateRange.length && firstDate === lastDate) {
			return false;
		}

		if (judgeDate === firstDate) {
			return judge.isBetween(from, from.clone().endOf('day'), undefined, pattern);
		}

		if (judgeDate === lastDate) {
			return judge.clone().add(1, 'days').isBetween(to.clone().startOf('day'), to, undefined, pattern);
		}

		return (
			judge.isBetween(from, from.clone().endOf('day'), undefined, pattern) ||
			judge.clone().add(1, 'days').isBetween(to.clone().startOf('day'), to, undefined, pattern)
		);
	}

	return judge.isBetween(from, to, undefined, pattern);
}

export function getDaysBetweenDates(startDate, endDate, format = DATE) {
	const dates = [];

	if (!startDate || !endDate) {
		return dates;
	}

	const now = moment(startDate);

	while (now.isSameOrBefore(endDate)) {
		dates.push(now.format(format));
		now.add(1, 'days');
	}

	return dates;
}
